// LIBRARY IMPORTS
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, Divider, Grid, InputAdornment, MenuItem, TextField, Typography, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import { mdiLaptop } from "@mdi/js";
// LOCAL IMPORTS
import { LiteralsContext, withLiterals } from "containers/shared/literals";
import { handleInfoDispatch } from "utils/info-dispatch-mvl";
import { MoreInfoContext } from "contexts/more_info.context";
import { esConceptoVivienda } from "utils/concepto.vivienda";
import { translate } from "utils/i18n";
import ConceptoIcon from "components/concepto.icon";
import TextNumber from "components/text-number";
import usePage from "hooks/page.hook";
import Term from "components/term";
// STYLES
import styles from "../../shared/pago-a-cuenta.styles";
import IoC from "contexts/ioc.context";
import { RecibosFlexiblesGateway } from "gateways/recibo-flexibles.gateway";
import { IReciboFlexible } from "gateways/recibo-flexibles.interfaces ";
import { useHistory } from "react-router";

type LocationState = { recibo: IReciboFlexible };
interface IParams {
  estado: "pendiente" | "pagado";
}
type Context = any;
type Props = RouteComponentProps<IParams, Context, LocationState>;

const useStyles = makeStyles(styles);

const PagoFlexiblePago: FC<Props> = ({ location, match }) => {
  const recibo = location.state.recibo;
  const estado = match.params;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const ioc = useContext(IoC);
  const gateway = useMemo(() => ioc.get(RecibosFlexiblesGateway) as RecibosFlexiblesGateway, [ioc]);
  // HOOKS
  const terms = useContext(LiteralsContext);
  const classes = useStyles();
  const history = useHistory();

  const [, infoDispatch] = useContext(MoreInfoContext);
  const [, pageDispatcher] = usePage();

  // LOCAL STATES
  const [importe, setImporte] = useState<undefined | number>(undefined);
  const [error, setError] = useState<{ error: boolean; message: string }>({ error: false, message: "" });

  // MEMOS
  const disableCarritoAction = useMemo(() => (error ? true : false), [error]);

  const handleClick = async () => {
    if (importe) {
      const result = await gateway.generarModelos(recibo.idReciboOrigen, importe);
      if (result.ok) {
        history.push("/pasarela-pago/pago/" + result.entidad, {
          ...estado,
          from: "PagoFlexible",
        });
      }
    }
  };

  // -------- USE EFFECT --------

  useEffect(() => {
    pageDispatcher({
      type: "setHeader",
      header: {
        icon: mdiLaptop,
        title: <Term component="CarteroVirtual" text={"TitlePago_flexible"} />,
        moreInfoTemplate: "Pago_flexible",
        right: (
          <MenuItem button onClick={() => handleShowMasInfo("Pago_flexible")}>
            <Term component="Global" text="Mas informacion" />
          </MenuItem>
        ),
      },
      menu: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDispatcher, terms]);

  // -------- HANDLERS --------

  const handleShowMasInfo = useCallback(
    (templateName: string) => {
      handleInfoDispatch(infoDispatch, "show-info", templateName);
    },
    [infoDispatch]
  );

  return (
    <Card className={classes.cardContainer}>
      {recibo ? (
        <>
          <Grid container direction="column">
            {/* TITLE */}

            <Grid item>
              {/* IMPORTANT  */}
              <Typography variant="h6" className={classes.title}>
                {translate("CarteroVirtual", "PagoACuentaTitle", terms).toUpperCase()}
              </Typography>
              <div className={classes.row}>
                <Typography variant="body1" className={classes.text}>
                  <Term component="CarteroVirtual" text="info_pago_flexible" />
                </Typography>
              </div>
            </Grid>

            {/* RECIBO */}

            <Grid item container direction="row" spacing={2} style={{ marginBottom: 15 }}>
              {/* TRIBUTO SELECCIONADO*/}

              <Grid item xs={6} container direction="column">
                <Typography className={classes.subTitle}>
                  <Term component="CarteroVirtual" text="tributoSeleccionado" />
                </Typography>
                <Divider className={classes.divider} />
                <div className={[classes.row, classes.content].join(" ")}>
                  <div className={classes.imageGrid}>
                    <div className={classes.imagenContainer}>
                      <ConceptoIcon concepto={recibo.concepto.idTipo} codigo={recibo.concepto.codigo} />
                    </div>
                  </div>
                  <div className={classes.column}>
                    <Typography className={classes.highlightText}>{recibo.organismo.nombre}</Typography>
                    <Typography className={classes.highlightText}>
                      <Term component="CarteroVirtual" text={recibo.concepto.nombre} />
                    </Typography>
                    {/* <Typography className={classes.highlightText}>
                                            {recibo.referencia}
                                        </Typography> */}
                    <Typography className={classes.detailText} style={{ marginTop: 10 }}>
                      {esConceptoVivienda(recibo.concepto.idTipo) ? recibo.direccionTributaria : recibo.objeto}
                    </Typography>
                  </div>
                </div>
              </Grid>

              {/*  IMPORTE PENDIENTE  */}

              <Grid item xs={6}>
                <Typography className={classes.subTitle}>
                  <Term component="CarteroVirtual" text="Pago_flexible_importe" />
                </Typography>
                <Divider className={classes.divider} />
                <div className={[classes.content].join(" ")}>
                  <TextNumber
                    component="span"
                    className={classes.price}
                    value={recibo.importeRecibo - recibo.importeFlexibleAcumulado}
                    options={{ isMoney: true, minDecimalLength: 2, maxDecimalLength: 2 }}
                  />
                </div>
              </Grid>
            </Grid>

            {/*  IMPORTE FLEXIBLE QUE DESEA PAGAR */}

            <Grid item container direction="row" spacing={2}>
              <Grid item xs={isMobile ? 10 : 6} container direction="column">
                <Typography className={classes.subTitle}>
                  <Term component="CarteroVirtual" text="ImportePagarFlexible" />
                </Typography>
                <Divider className={classes.divider} />
                <div className={[classes.content].join(" ")} style={{ display: "flex", justifyContent: "space-between", marginTop: 10, gap: 16 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="number"
                    label={translate("CarteroVirtual", "Importe", terms)}
                    value={importe}
                    onChange={(e) => {
                      const importePendiente = recibo.importeRecibo - recibo.importeFlexibleAcumulado;
                      const resto = Number((importePendiente - (parseFloat(e.target.value) ?? 0)).toFixed(2));
                      if (e.target.value && parseFloat(e.target.value) > recibo.importeRecibo - recibo.importeFlexibleAcumulado) {
                        setError({ error: true, message: "ImporteError" });
                      } else if (resto < 1 && resto > 0) {
                        setError({ error: true, message: "ImporteErrorResto" });
                      } else {
                        setError({ error: false, message: "" });
                      }
                      setImporte(e.target.value ? parseFloat(e.target.value) : undefined);
                    }}
                    error={error.error}
                    helperText={error.error ? translate("CarteroVirtual", error.message, terms) : ""}
                    inputProps={{
                      step: 0.01,
                      min: 0,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                  />
                  <Button
                    onClick={handleClick}
                    variant="contained"
                    color="primary"
                    disabled={
                      importe === undefined ||
                      importe < 1 ||
                      importe > recibo.importeRecibo - recibo.importeFlexibleAcumulado ||
                      (Number((recibo.importeRecibo - recibo.importeFlexibleAcumulado - importe).toFixed(2)) < 1 &&
                        Number((recibo.importeRecibo - recibo.importeFlexibleAcumulado - importe).toFixed(2)) > 0)
                    }
                  >
                    <Term component="Global" text="Pagar" />
                  </Button>
                </div>
              </Grid>
              <Grid item xs={6}></Grid>
            </Grid>
          </Grid>

          {/* 
                    Quito el carrito, sustituir por un boton de "Pagar" porque solo hay 1 recibo
                    <Carrito
                        items={[recibo]}
                        visible={true}
                        pendiente={estado === 'pendiente'}
                        isFromConsultasExternas={false}
                        disableButton={disableCarritoAction}
                        importeAbonado={importe}
                        fullWidth
                    /> */}
        </>
      ) : (
        <Typography>
          <Term component="CarteroVirtual" text="ErrorCargaRecibo" />
        </Typography>
      )}
    </Card>
  );
};

export default withLiterals(["Global", "CarteroVirtual", "Domiciliaciones"])(PagoFlexiblePago);
